"use client";

import { useState } from "react";
import { useSearchParams } from "next/navigation";
import { useT } from "@/hooks/useT";

type TimeWindow = "1d" | "7d" | "30d" | "all";

interface WindowMetrics {
  realized_pnl: number;
  total_pnl: number;
  volume: number;
  roi: number;
  trades: number;
  wins: number;
  losses: number;
  win_rate: number;
  profit_factor: number;
  positions_closed: number;
  avg_buy_price: number | null;
  avg_sell_price: number | null;
  fees_paid: number;
  fees_refunded: number;
  avg_hold_time_seconds?: number;
  wallet_age_days?: number;
  total_positions?: number;
  active_positions?: number;
  max_win_streak?: number;
  max_loss_streak?: number;
  best_position_realized_pnl?: number;
  worst_position_realized_pnl?: number;
}

interface PredexonProfile {
  metrics?: {
    one_day?: WindowMetrics;
    seven_day?: WindowMetrics;
    thirty_day?: WindowMetrics;
    all_time?: WindowMetrics;
  };
  trading_styles?: {
    primary_style: string;
    is_whale: boolean;
    is_market_maker: boolean;
    is_active_trader: boolean;
    is_buy_and_hold: boolean;
    is_degen: boolean;
    is_high_conviction: boolean;
    is_contrarian: boolean;
    is_value_hunter: boolean;
  };
  entry_edge?: number;
}

const WINDOW_KEYS: { key: TimeWindow; label: string; metricsKey: string }[] = [
  { key: "1d", label: "1D", metricsKey: "one_day" },
  { key: "7d", label: "7D", metricsKey: "seven_day" },
  { key: "30d", label: "30D", metricsKey: "thirty_day" },
  { key: "all", label: "ALL", metricsKey: "all_time" },
];

// Inline qualifier shown next to the headline PnL so the number is never
// ambiguous about its window. This is what stops "the leaderboard says +$5k,
// the profile says +$22M" reading as a contradiction — the leaderboard's
// number is 24H, this one is ALL-TIME, and now the figure says so.
const WINDOW_QUALIFIER: Record<TimeWindow, string> = {
  "1d": "24H",
  "7d": "7D",
  "30d": "30D",
  "all": "ALL-TIME",
};

const VALID_WINDOWS: TimeWindow[] = ["1d", "7d", "30d", "all"];

const STYLE_FLAG_KEYS: { key: string; tKey: string; icon: string }[] = [
  { key: "is_whale", tKey: "traders.psp.style.whale", icon: "W" },
  { key: "is_market_maker", tKey: "traders.psp.style.marketMaker", icon: "MM" },
  { key: "is_active_trader", tKey: "traders.psp.style.active", icon: "AT" },
  { key: "is_buy_and_hold", tKey: "traders.psp.style.holder", icon: "BH" },
  { key: "is_degen", tKey: "traders.psp.style.degen", icon: "DG" },
  { key: "is_high_conviction", tKey: "traders.psp.style.conviction", icon: "HC" },
  { key: "is_contrarian", tKey: "traders.psp.style.contrarian", icon: "CT" },
  { key: "is_value_hunter", tKey: "traders.psp.style.value", icon: "VH" },
];

function fU(v: number): string {
  const a = Math.abs(v);
  if (a >= 1_000_000) return `$${(v / 1_000_000).toFixed(2)}M`;
  if (a >= 1_000) return `$${(v / 1_000).toFixed(1)}K`;
  return `$${v.toFixed(0)}`;
}
function fS(v: number): string { const s = fU(v); return v > 0 ? `+${s}` : s; }
function fD(s: number): string { const d = s / 86400; return d >= 1 ? `${d.toFixed(1)}d` : `${(s / 3600).toFixed(1)}h`; }
function cl(v: number): string { return v >= 0 ? "text-[#00FF9D]" : "text-[#FF4444]"; }

/** Compact stat row for sidebar */
function StatRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between py-1.5">
      <span className="text-[11px] text-gray-500">{label}</span>
      <span className="font-monument text-[13px] font-bold">{children}</span>
    </div>
  );
}

export default function PredexonStatsPanel({ profile, compact = false }: { profile: PredexonProfile | null; compact?: boolean }) {
  const t = useT();
  const WINDOWS = WINDOW_KEYS;
  // Carry-through: open on the window the deep-link asks for (e.g. a
  // leaderboard linking with `?window=1d` so the profile matches the row the
  // user clicked). Dormant until an origin actually passes the param — today
  // the DexTools widget doesn't, so this defaults to ALL. NOTE: `window`
  // shadows the global — only use `useSearchParams` here, never window.location.
  const searchParams = useSearchParams();
  const urlWindow = searchParams?.get("window") as TimeWindow | null;
  const initialWindow: TimeWindow = urlWindow && VALID_WINDOWS.includes(urlWindow) ? urlWindow : "all";
  const [window, setWindow] = useState<TimeWindow>(initialWindow);

  if (!profile?.metrics) return null;

  const currentKey = WINDOWS.find((w) => w.key === window)!.metricsKey;
  const m = (profile.metrics as any)?.[currentKey] as WindowMetrics | undefined;

  if (!m) return null;

  const isAllTime = window === "all";
  const pnlPct = (m.roi * 100).toFixed(1);
  const styles = profile.trading_styles;
  const activeFlags = styles
    ? STYLE_FLAG_KEYS.filter(f => (styles as any)[f.key]).map(f => ({ ...f, label: t(f.tKey) }))
    : [];

  // ─── COMPACT MODE (right sidebar, ~280px) ───
  if (compact) {
    return (
      <div className="bg-white dark:bg-[#18181D] rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] overflow-hidden">
        {/* Hero PnL */}
        <div className="px-4 pt-4 pb-3">
          <div className="text-[10px] font-medium uppercase tracking-wider text-gray-500 mb-1">{t("traders.psp.section.performance")}</div>
          <div className="flex items-baseline gap-2 mb-3">
            <span className={`font-monument text-2xl font-black tracking-tight ${cl(m.total_pnl)}`}>
              {fS(m.total_pnl)}
            </span>
            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-[5px] ${m.roi >= 0 ? "bg-[#00FF9D]/10 text-[#00FF9D]" : "bg-[#FF4444]/10 text-[#FF4444]"}`}>
              {m.roi >= 0 ? "+" : ""}{pnlPct}%
            </span>
            <span className="ml-auto text-[10px] font-bold uppercase tracking-wider text-gray-400 dark:text-gray-500">
              {WINDOW_QUALIFIER[window]}
            </span>
          </div>
          {/* Time Toggle */}
          <div className="flex items-center bg-gray-100 dark:bg-white/[0.04] rounded-[5px] p-0.5">
            {WINDOWS.map((w) => (
              <button
                key={w.key}
                onClick={() => setWindow(w.key)}
                className={`flex-1 px-2 py-1 text-[10px] font-bold rounded-[5px] transition-all ${
                  window === w.key
                    ? "bg-white dark:bg-white/[0.12] text-gray-900 dark:text-white shadow-sm"
                    : "text-gray-400 hover:text-gray-300"
                }`}
              >
                {w.label}
              </button>
            ))}
          </div>
        </div>

        {/* Stats list */}
        <div className="px-4 pb-2 border-t border-gray-100 dark:border-white/[0.04]">
          <StatRow label={t("traders.psp.row.volume")}><span className="text-white">{fU(m.volume)}</span></StatRow>
          <StatRow label={t("traders.psp.row.winRate")}><span className="text-white">{(m.win_rate * 100).toFixed(1)}%</span></StatRow>
          <StatRow label={t("traders.psp.row.wl")}>
            <span><span className="text-[#00FF9D]">{m.wins}</span><span className="text-gray-600 mx-0.5">/</span><span className="text-[#FF4444]">{m.losses}</span></span>
          </StatRow>
          <StatRow label={t("traders.psp.row.trades")}><span className="text-white">{m.trades.toLocaleString()}</span></StatRow>
          <StatRow label={t("traders.psp.row.profitFactor")}><span className={cl(m.profit_factor - 1)}>{m.profit_factor.toFixed(2)}</span></StatRow>
          {profile.entry_edge != null && (
            <StatRow label={t("traders.psp.row.entryEdge")}>
              <span className={profile.entry_edge <= 0 ? "text-[#00FF9D]" : "text-[#FF4444]"}>
                {profile.entry_edge <= 0 ? "" : "+"}{(profile.entry_edge * 100).toFixed(1)}%
              </span>
            </StatRow>
          )}
          {(m.fees_paid - (m.fees_refunded || 0)) > 0 && (
            <StatRow label={t("traders.psp.row.netFees")}><span className="text-gray-400">{fU(m.fees_paid - (m.fees_refunded || 0))}</span></StatRow>
          )}
        </div>

        {/* All-Time Extras */}
        {isAllTime && (m.best_position_realized_pnl != null || m.avg_hold_time_seconds != null) && (
          <div className="px-4 pb-3 border-t border-gray-100 dark:border-white/[0.04]">
            {m.best_position_realized_pnl != null && (
              <StatRow label={t("traders.psp.row.bestTrade")}><span className="text-[#00FF9D]">{fU(m.best_position_realized_pnl)}</span></StatRow>
            )}
            {m.worst_position_realized_pnl != null && (
              <StatRow label={t("traders.psp.row.worstTrade")}><span className="text-[#FF4444]">{fS(m.worst_position_realized_pnl)}</span></StatRow>
            )}
            {m.max_win_streak != null && (
              <StatRow label={t("traders.psp.row.streaks")}>
                <span><span className="text-[#00FF9D]">W{m.max_win_streak}</span>{m.max_loss_streak != null && <><span className="text-gray-600 mx-0.5">/</span><span className="text-[#FF4444]">L{m.max_loss_streak}</span></>}</span>
              </StatRow>
            )}
            {m.avg_hold_time_seconds != null && (
              <StatRow label={t("traders.psp.row.avgHold")}><span className="text-white">{fD(m.avg_hold_time_seconds)}</span></StatRow>
            )}
            {m.wallet_age_days != null && (
              <StatRow label={t("traders.psp.row.walletAge")}><span className="text-white">{m.wallet_age_days}d</span></StatRow>
            )}
            {m.total_positions != null && (
              <StatRow label={t("traders.psp.row.positions")}>
                <span className="text-white">{m.total_positions}{m.active_positions != null && <span className="text-gray-500 text-[10px] ml-1">({m.active_positions})</span>}</span>
              </StatRow>
            )}
          </div>
        )}

        {/* Trading Style Flags */}
        {activeFlags.length > 0 && (
          <div className="px-4 py-3 border-t border-gray-100 dark:border-white/[0.04]">
            <div className="text-[10px] font-medium uppercase tracking-wider text-gray-500 mb-2">{t("traders.psp.section.tradingStyle")}</div>
            <div className="flex flex-wrap gap-1.5">
              {activeFlags.map(f => (
                <span key={f.key} className={`px-2 py-1 rounded-[5px] text-[10px] font-bold ${
                  f.key === 'is_whale' ? 'bg-blue-500/10 text-blue-400' :
                  f.key === 'is_degen' ? 'bg-red-500/10 text-red-400' :
                  f.key === 'is_high_conviction' ? 'bg-amber-500/10 text-amber-400' :
                  f.key === 'is_contrarian' ? 'bg-purple-500/10 text-purple-400' :
                  f.key === 'is_value_hunter' ? 'bg-emerald-500/10 text-emerald-400' :
                  'bg-white/[0.06] text-gray-300'
                }`}>
                  {f.label}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  // ─── FULL-WIDTH MODE (mobile, or if used outside sidebar) ───
  return (
    <div className="bg-white dark:bg-[#18181D] rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] overflow-hidden">
      {/* Hero */}
      <div className="px-5 pt-5 pb-4 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <div className="text-[11px] font-medium uppercase tracking-wider text-gray-500 mb-1">{t("traders.psp.section.performance")}</div>
          <div className="flex items-baseline gap-3">
            <span className={`font-monument text-3xl font-black tracking-tight ${cl(m.total_pnl)}`}>{fS(m.total_pnl)}</span>
            <span className={`text-sm font-semibold px-2 py-0.5 rounded-[5px] ${m.roi >= 0 ? "bg-[#00FF9D]/10 text-[#00FF9D]" : "bg-[#FF4444]/10 text-[#FF4444]"}`}>
              {m.roi >= 0 ? "+" : ""}{pnlPct}%
            </span>
            <span className="text-[11px] font-bold uppercase tracking-wider text-gray-400 dark:text-gray-500">
              {WINDOW_QUALIFIER[window]}
            </span>
          </div>
        </div>
        <div className="flex items-center bg-gray-100 dark:bg-white/[0.04] rounded-[5px] p-0.5">
          {WINDOWS.map((w) => (
            <button key={w.key} onClick={() => setWindow(w.key)}
              className={`px-3.5 py-1.5 text-[11px] font-bold rounded-[5px] transition-all ${window === w.key ? "bg-white dark:bg-white/[0.12] text-gray-900 dark:text-white shadow-sm" : "text-gray-400 hover:text-gray-300"}`}>
              {w.label}
            </button>
          ))}
        </div>
      </div>

      <div className="border-t border-gray-100 dark:border-white/[0.04]" />

      {/* Stats Grid */}
      <div className="px-5 py-4 grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-4">
        <div><div className="text-[10px] font-medium text-gray-500 uppercase tracking-wider mb-1">{t("traders.psp.row.volume")}</div><div className="font-monument text-[15px] font-bold text-white">{fU(m.volume)}</div></div>
        <div><div className="text-[10px] font-medium text-gray-500 uppercase tracking-wider mb-1">{t("traders.psp.row.winRate")}</div><div className="font-monument text-[15px] font-bold text-white">{(m.win_rate * 100).toFixed(1)}%</div></div>
        <div><div className="text-[10px] font-medium text-gray-500 uppercase tracking-wider mb-1">{t("traders.psp.row.wl")}</div><div className="font-monument text-[15px] font-bold"><span className="text-[#00FF9D]">{m.wins}</span><span className="text-gray-600 mx-0.5">/</span><span className="text-[#FF4444]">{m.losses}</span></div></div>
        <div><div className="text-[10px] font-medium text-gray-500 uppercase tracking-wider mb-1">{t("traders.psp.row.trades")}</div><div className="font-monument text-[15px] font-bold text-white">{m.trades.toLocaleString()}</div></div>
        <div><div className="text-[10px] font-medium text-gray-500 uppercase tracking-wider mb-1">{t("traders.psp.row.profitFactor")}</div><div className={`font-monument text-[15px] font-bold ${cl(m.profit_factor - 1)}`}>{m.profit_factor.toFixed(2)}</div></div>
        {profile.entry_edge != null && <div><div className="text-[10px] font-medium text-gray-500 uppercase tracking-wider mb-1">{t("traders.psp.row.entryEdge")}</div><div className={`font-monument text-[15px] font-bold ${profile.entry_edge <= 0 ? "text-[#00FF9D]" : "text-[#FF4444]"}`}>{profile.entry_edge <= 0 ? "" : "+"}{(profile.entry_edge * 100).toFixed(1)}%</div></div>}
        {(m.fees_paid - (m.fees_refunded || 0)) > 0 && <div><div className="text-[10px] font-medium text-gray-500 uppercase tracking-wider mb-1">{t("traders.psp.row.netFees")}</div><div className="font-monument text-[15px] font-bold text-gray-400">{fU(m.fees_paid - (m.fees_refunded || 0))}</div></div>}
      </div>

      {/* All-Time Extras */}
      {isAllTime && (m.best_position_realized_pnl != null || m.avg_hold_time_seconds != null) && (
        <>
          <div className="border-t border-gray-100 dark:border-white/[0.04]" />
          <div className="px-5 py-3.5 grid grid-cols-3 sm:grid-cols-6 gap-x-6 gap-y-3">
            {m.best_position_realized_pnl != null && <div><div className="text-[10px] font-medium text-gray-500 uppercase tracking-wider mb-0.5">{t("traders.psp.row.bestTrade")}</div><div className="font-monument text-[13px] font-bold text-[#00FF9D]">{fU(m.best_position_realized_pnl)}</div></div>}
            {m.worst_position_realized_pnl != null && <div><div className="text-[10px] font-medium text-gray-500 uppercase tracking-wider mb-0.5">{t("traders.psp.row.worstTrade")}</div><div className="font-monument text-[13px] font-bold text-[#FF4444]">{fS(m.worst_position_realized_pnl)}</div></div>}
            {m.max_win_streak != null && <div><div className="text-[10px] font-medium text-gray-500 uppercase tracking-wider mb-0.5">{t("traders.psp.row.streaks")}</div><div className="font-monument text-[13px] font-bold"><span className="text-[#00FF9D]">W{m.max_win_streak}</span>{m.max_loss_streak != null && <><span className="text-gray-500 mx-0.5">/</span><span className="text-[#FF4444]">L{m.max_loss_streak}</span></>}</div></div>}
            {m.avg_hold_time_seconds != null && <div><div className="text-[10px] font-medium text-gray-500 uppercase tracking-wider mb-0.5">{t("traders.psp.row.avgHold")}</div><div className="font-monument text-[13px] font-bold text-white">{fD(m.avg_hold_time_seconds)}</div></div>}
            {m.wallet_age_days != null && <div><div className="text-[10px] font-medium text-gray-500 uppercase tracking-wider mb-0.5">{t("traders.psp.row.walletAge")}</div><div className="font-monument text-[13px] font-bold text-white">{m.wallet_age_days}d</div></div>}
            {m.total_positions != null && <div><div className="text-[10px] font-medium text-gray-500 uppercase tracking-wider mb-0.5">{t("traders.psp.row.positions")}</div><div className="font-monument text-[13px] font-bold text-white">{m.total_positions}{m.active_positions != null && <span className="text-[11px] text-gray-500 ml-1">({m.active_positions})</span>}</div></div>}
          </div>
        </>
      )}

      {/* Style Flags */}
      {activeFlags.length > 0 && (
        <>
          <div className="border-t border-gray-100 dark:border-white/[0.04]" />
          <div className="px-5 py-3 flex flex-wrap gap-1.5">
            {activeFlags.map(f => (
              <span key={f.key} className={`px-2 py-1 rounded-[5px] text-[10px] font-bold ${
                f.key === 'is_whale' ? 'bg-blue-500/10 text-blue-400' :
                f.key === 'is_degen' ? 'bg-red-500/10 text-red-400' :
                f.key === 'is_high_conviction' ? 'bg-amber-500/10 text-amber-400' :
                f.key === 'is_contrarian' ? 'bg-purple-500/10 text-purple-400' :
                f.key === 'is_value_hunter' ? 'bg-emerald-500/10 text-emerald-400' :
                'bg-white/[0.06] text-gray-300'
              }`}>{f.label}</span>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
